import { motion } from 'framer-motion';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useEffect, useRef, useState } from 'react';
import { useLang } from '@/i18n/LanguageContext';
import { Navigation, Radio } from 'lucide-react';

// Corredores reais (Brasil + América Latina)
const ROUTES = [
  [[-23.5505, -46.6333], [-24.9500, -49.2733], [-25.4284, -49.2733], [-26.20, -48.80], [-26.9133, -48.5553]], // SP → Curitiba → Porto Belo
  [[-26.9133, -48.5553], [-26.30, -49.90], [-25.80, -52.30], [-25.5163, -54.6167]], // Porto Belo → CDE
  [[-25.5163, -54.6167], [-27.40, -55.90], [-30.03, -57.90], [-32.94, -60.65], [-34.6037, -58.3816]], // CDE → Rosario → Buenos Aires
  [[-34.6037, -58.3816], [-34.75, -56.30], [-34.9011, -56.1645]], // BA → Montevideo
  [[-25.5163, -54.6167], [-24.80, -55.50], [-25.2637, -57.5759]], // CDE → Asunción
];

const CITY_POINTS = [
  { name: 'São Paulo', lat: -23.5505, lng: -46.6333 },
  { name: 'Porto Belo', lat: -26.9133, lng: -48.5553 },
  { name: 'Ciudad del Este', lat: -25.5163, lng: -54.6167 },
  { name: 'Asunción', lat: -25.2637, lng: -57.5759 },
  { name: 'Buenos Aires', lat: -34.6037, lng: -58.3816 },
  { name: 'Montevideo', lat: -34.9011, lng: -56.1645 },
];

const dist = (a, b) => Math.hypot(a[0] - b[0], a[1] - b[1]);

const pathLength = (pts) => {
  let total = 0;
  for (let i = 0; i < pts.length - 1; i++) total += dist(pts[i], pts[i + 1]);
  return total;
};

const pointAt = (pts, t) => {
  const total = pathLength(pts);
  let goal = total * t;
  for (let i = 0; i < pts.length - 1; i++) {
    const d = dist(pts[i], pts[i + 1]);
    if (goal <= d) {
      const k = d === 0 ? 0 : goal / d;
      return [pts[i][0] + (pts[i + 1][0] - pts[i][0]) * k, pts[i][1] + (pts[i + 1][1] - pts[i][1]) * k];
    }
    goal -= d;
  }
  return pts[pts.length - 1];
};

const vehicleIcon = (color) => L.divIcon({
  className: '',
  iconSize: [18, 18],
  html: `<div style="position:relative;width:18px;height:18px">
    <span style="position:absolute;inset:0;border-radius:9999px;background:${color};opacity:.45;animation:rt-sonar 2.2s cubic-bezier(0,0,.2,1) infinite"></span>
    <span style="position:absolute;left:5px;top:5px;width:8px;height:8px;border-radius:9999px;background:${color};border:1.5px solid #fff;box-shadow:0 1px 4px rgba(10,36,64,.4)"></span>
  </div>`,
});

const COLORS = { moving: '#0FA968', stopped: '#64748B', ignition: '#1E9FE0', alert: '#EF4444' };

export default function LeafletMap() {
  const { content } = useLang();
  const hero = content.hero;
  const ref = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const [active, setActive] = useState(0);
  const [tick, setTick] = useState(0);

  const STATUS = content.mapStatuses;
  const STATUS_KEYS = ['moving', 'stopped', 'ignition', 'alert'];

  const vehicles = ROUTES.map((r, i) => ({
    id: i,
    plate: `RDT·${(7 + i * 3)}A${(42 + i * 17) % 100 < 10 ? '0' : ''}${(42 + i * 17) % 100}`,
    route: r,
    t: i * 0.14,
    dir: 1,
    speed: 62 + i * 21,
    statusKey: STATUS_KEYS[i % 4],
  }));

  useEffect(() => {
    if (!ref.current || mapRef.current) return;
    const map = L.map(ref.current, {
      center: [-24, -57],
      zoom: 3,
      zoomControl: true,
      scrollWheelZoom: false,
      attributionControl: true,
      worldCopyJump: true,
    });
    mapRef.current = map;
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap',
      maxZoom: 18,
    }).addTo(map);

    ROUTES.forEach((pts) => {
      L.polyline(pts, { color: '#1E9FE0', weight: 2, opacity: 0.55, dashArray: '5 7' }).addTo(map);
    });
    CITY_POINTS.forEach((c) => {
      L.circleMarker([c.lat, c.lng], { radius: 3.5, color: '#0A2440', weight: 1, fillColor: '#1E9FE0', fillOpacity: 1 }).addTo(map)
        .bindTooltip(c.name, { direction: 'top', className: 'rt-tooltip' });
    });

    markersRef.current = vehicles.map((v) => {
      const [lat, lng] = pointAt(v.route, v.t);
      return L.marker([lat, lng], { icon: vehicleIcon(COLORS[v.statusKey]) }).addTo(map)
        .bindTooltip(`${v.plate} · ${v.speed} km/h`, { direction: 'top', offset: [0, -10], className: 'rt-tooltip' });
    });

    map.fitBounds([[-46, -76], [8, -34]], { padding: [8, 8] });

    const interval = setInterval(() => {
      vehicles.forEach((v, i) => {
        v.t += 0.0022 * (v.dir);
        if (v.t >= 1) { v.t = 1; v.dir = -1; }
        if (v.t <= 0) { v.t = 0; v.dir = 1; }
        const pos = pointAt(v.route, v.t);
        markersRef.current[i] && markersRef.current[i].setLatLng(pos);
      });
      setTick((x) => x + 1);
    }, 80);

    const cycle = setInterval(() => {
      setActive((a) => (a + 1) % vehicles.length);
      vehicles.forEach((v, i) => {
        const idx = (i + Math.floor(Date.now() / 9000)) % 4;
        v.statusKey = STATUS_KEYS[idx];
        v.speed = v.statusKey === 'stopped' ? 0 : 58 + ((i * 37) % 60);
        markersRef.current[i] && markersRef.current[i].setIcon(vehicleIcon(COLORS[v.statusKey]));
      });
    }, 5000);

    return () => {
      clearInterval(interval);
      clearInterval(cycle);
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const av = vehicles[active] || vehicles[0];
  const statusLabel = STATUS[av.statusKey] || STATUS.moving;

  return (
    <div data-testid="hero-leaflet-map" className="relative overflow-hidden rounded-3xl border border-brand-line bg-white shadow-[0_40px_80px_-32px_rgba(7,27,48,0.45)]">
      <div ref={ref} className="h-[420px] w-full lg:h-[540px]" />

      <div data-testid="hero-live-badge" className="pointer-events-none absolute left-4 top-4 z-[500] inline-flex items-center gap-2 rounded-full border border-white/40 bg-white/85 px-3.5 py-1.5 backdrop-blur-md">
        <span className="h-2 w-2 animate-blink rounded-full bg-emerald-500" />
        <span className="font-mono text-[10px] font-semibold tracking-[0.18em] text-brand-navy">{hero.liveBadge}</span>
      </div>

      <p className="pointer-events-none absolute bottom-4 left-1/2 z-[500] -translate-x-1/2 whitespace-nowrap rounded-full bg-brand-navy/85 px-4 py-1.5 font-mono text-[10px] tracking-[0.14em] text-white/90 backdrop-blur-md">
        {hero.mapCaption}
      </p>

      <motion.div
        data-testid="hero-vehicle-card"
        className="absolute bottom-14 right-4 z-[500] w-[215px] rounded-2xl border border-brand-line bg-white/95 p-4 shadow-[0_16px_40px_-16px_rgba(10,36,64,0.5)] backdrop-blur-md lg:bottom-16"
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="flex items-center justify-between">
          <span className="font-mono text-[12px] font-bold tracking-wider text-brand-navy">{av.plate}</span>
          <span className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-semibold" style={{ background: `${COLORS[av.statusKey]}1A`, color: COLORS[av.statusKey] }}>
            <span className="h-1 w-1 rounded-full" style={{ background: COLORS[av.statusKey] }} />
            {statusLabel}
          </span>
        </div>
        <div className="mt-3 flex items-end justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-brand-muted">km/h</p>
            <p className="font-mono text-2xl font-bold leading-none text-brand-navy">{av.speed}</p>
          </div>
          <Navigation className="h-5 w-5 text-brand-blue" />
        </div>
        <div className="mt-3 h-1 overflow-hidden rounded-full bg-brand-ice">
          <div className="h-full rounded-full bg-gradient-to-r from-brand-blue to-brand-cyan" style={{ width: `${Math.min(100, av.speed)}%` }} />
        </div>
        <p className="mt-2.5 flex items-center gap-1.5 text-[10px] text-brand-muted">
          <Radio className="h-3 w-3 text-brand-blue" />
          GPS · SAT · GSM
        </p>
      </motion.div>

      <style>{`
        @keyframes rt-sonar { 0% { transform: scale(1); opacity:.45 } 100% { transform: scale(2.4); opacity:0 } }
        .rt-tooltip { font-family:'JetBrains Mono', monospace !important; font-size:10px !important; background:#0A2440 !important; color:#fff !important; border:none !important; border-radius:6px !important; padding:3px 8px !important; box-shadow:0 8px 20px -8px rgba(10,36,64,.5) }
        .leaflet-container { font-family:'Instrument Sans', sans-serif }
        .leaflet-control-attribution { font-size:9px !important }
      `}</style>
    </div>
  );
}